import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Save, Loader2, X, Tag } from "lucide-react";
import api from "@/lib/api";
import { invalidateQualificationTagsCache } from "@/hooks/useQualificationTags";

const LIST_META = {
  service_types: {
    title: "Tipos de Serviço",
    description: "Tags exibidas no ecrã de qualificação inicial na secção \"Tipo de serviço pretendido\" (chips).",
    placeholder: "Ex: Cuidados paliativos",
  },
  needs: {
    title: "Necessidades",
    description: "Tags exibidas na secção \"Necessidades\" da qualificação inicial.",
    placeholder: "Ex: Estimulação Cognitiva",
  },
};

const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary focus:ring-1 focus:ring-[#1D4ED8]/30";
const btnSm = "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors";

function TagRow({ tag, onSaved, onDeleted, onMove }) {
  const [value, setValue] = useState(tag.value || "");
  const [label, setLabel] = useState(tag.label || tag.value || "");
  const [active, setActive] = useState(tag.active !== false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    if (!value.trim()) { toast.error("Valor obrigatório"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/qualification-tags/${tag.id}`, {
        value: value.trim(),
        label: (label || value).trim(),
        active,
      });
      toast.success("Guardado");
      setDirty(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a guardar");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/qualification-tags/${tag.id}`, { active: !active });
      setActive(!active);
      onSaved?.(r.data);
      toast.success(!active ? "Tag reactivada" : "Tag desactivada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Eliminar a tag "${tag.value}"?\n\nNão vai afectar leads antigos, mas deixa de estar disponível para novos.`)) return;
    setBusy(true);
    try {
      await api.delete(`/qualification-tags/${tag.id}`);
      toast.success("Tag eliminada");
      onDeleted?.(tag.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a eliminar");
      setBusy(false);
    }
  };

  return (
    <tr data-testid={`qtag-row-${tag.id}`} className={`border-t border-border ${active ? "" : "bg-muted/40"}`}>
      <td className="p-2 w-8">
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => onMove(-1)} className="text-muted-foreground hover:text-foreground" disabled={busy} title="Subir"><ArrowUp className="w-3 h-3" /></button>
          <button type="button" onClick={() => onMove(1)} className="text-muted-foreground hover:text-foreground" disabled={busy} title="Descer"><ArrowDown className="w-3 h-3" /></button>
        </div>
      </td>
      <td className="p-2">
        <input
          data-testid={`qtag-value-${tag.id}`}
          className={inp}
          value={value}
          onChange={(e) => { setValue(e.target.value); setDirty(true); }}
          disabled={busy}
        />
      </td>
      <td className="p-2">
        <input
          data-testid={`qtag-label-${tag.id}`}
          className={inp}
          placeholder="Igual ao valor"
          value={label}
          onChange={(e) => { setLabel(e.target.value); setDirty(true); }}
          disabled={busy}
        />
      </td>
      <td className="p-2">
        {tag.is_seed ? (
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Seed</span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--success))] font-semibold">Custom</span>
        )}
      </td>
      <td className="p-2 whitespace-nowrap">
        <div className="flex items-center gap-1">
          {dirty && (
            <button
              data-testid={`qtag-save-${tag.id}`}
              onClick={save}
              disabled={busy}
              className={`${btnSm} bg-foreground hover:bg-foreground/90 text-background disabled:opacity-50`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Guardar
            </button>
          )}
          <button
            data-testid={`qtag-toggle-${tag.id}`}
            onClick={toggleActive}
            disabled={busy}
            className={`${btnSm} ${active ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] hover:bg-emerald-200" : "bg-muted text-muted-foreground hover:bg-slate-300"} disabled:opacity-50`}
          >
            {active ? <><Eye className="w-3 h-3" /> Activa</> : <><EyeOff className="w-3 h-3" /> Inactiva</>}
          </button>
          <button
            data-testid={`qtag-delete-${tag.id}`}
            onClick={doDelete}
            disabled={busy}
            className={`${btnSm} bg-[hsl(var(--danger-bg))] hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border border-[hsl(var(--danger))]/25 disabled:opacity-50`}
            title="Eliminar tag"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TagListSection({ listKey, tags, onChanged }) {
  const meta = LIST_META[listKey];
  const [newValue, setNewValue] = useState("");
  const [adding, setAdding] = useState(false);

  const doAdd = async () => {
    const v = (newValue || "").trim();
    if (!v) { toast.error("Escreva um valor"); return; }
    setAdding(true);
    try {
      await api.post("/qualification-tags", { list_key: listKey, value: v });
      toast.success(`Tag "${v}" adicionada`);
      setNewValue("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a adicionar");
    } finally {
      setAdding(false);
    }
  };

  const doMove = async (idx, delta) => {
    const next = [...tags];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    try {
      await api.post("/qualification-tags/reorder", {
        list_key: listKey,
        ordered_ids: next.map((t) => t.id),
      });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a reordenar");
    }
  };

  return (
    <section
      data-testid={`qtag-section-${listKey}`}
      className="bg-card border border-border rounded-2xl p-6 lg:p-8 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
          <Tag className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="font-ui text-xl text-foreground">{meta.title}</h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{meta.description}</p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold px-2.5 py-1 rounded-full bg-muted border border-border">
          {tags.length} tags
        </span>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          data-testid={`qtag-new-${listKey}`}
          className={inp}
          placeholder={meta.placeholder}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAdd(); } }}
          disabled={adding}
        />
        <button
          data-testid={`qtag-add-${listKey}`}
          onClick={doAdd}
          disabled={adding || !newValue.trim()}
          className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 text-background rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Adicionar
        </button>
      </div>

      {tags.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">Sem tags nesta lista.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                <th className="p-2 w-8"></th>
                <th className="p-2">Valor</th>
                <th className="p-2">Rótulo (opcional)</th>
                <th className="p-2 w-20">Origem</th>
                <th className="p-2 w-64">Acções</th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag, idx) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  onSaved={() => onChanged?.()}
                  onDeleted={() => onChanged?.()}
                  onMove={(delta) => doMove(idx, delta)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function QualificationTagsSettings() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ service_types: [], needs: [] });

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/qualification-tags");
      setState(r.data || { service_types: [], needs: [] });
      // Purge the app-wide cache so LeadNew/LeadScript pick up changes
      // without a hard refresh.
      invalidateQualificationTagsCache();
    } catch (e) {
      toast.error("Erro a carregar tags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto space-y-6" data-testid="qualification-tags-settings">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configuração</div>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight">Tags da qualificação</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Gere as listas de tags exibidas nos ecrãs "Tipo de serviço pretendido" e "Necessidades".
            Tags desactivadas ficam escondidas de novos leads mas mantêm-se em leads antigos.
          </p>
        </div>
        <button
          data-testid="qtag-reload"
          onClick={reload}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 font-semibold disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Recarregar
        </button>
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground italic p-8 text-center">A carregar…</div>
      ) : (
        <>
          <TagListSection listKey="service_types" tags={state.service_types || []} onChanged={reload} />
          <TagListSection listKey="needs" tags={state.needs || []} onChanged={reload} />
        </>
      )}
    </div>
  );
}
